import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Truck, MapPin, Phone, Trash2, RefreshCw, Navigation, Clock } from "lucide-react";

const COLS = [
  { id: "new", label: "Neu" },
  { id: "assigned", label: "Zugewiesen" },
  { id: "in_transit", label: "Unterwegs" },
  { id: "delivered", label: "Zugestellt" },
];

const empty = { customer_name: "", address: "", phone: "", notes: "", items: "", priority: "normal", status: "new", distance_km: 0, latitude: null, longitude: null, assigned_driver_id: null, delivery_time: "", delivery_date: "" };

export default function DeliveryPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(empty);
  const [sortBy, setSortBy] = useState("distance");
  const [syncing, setSyncing] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [aposoftEnabled, setAposoftEnabled] = useState(false);

  const load = async () => {
    const [r, u, b] = await Promise.all([
      api.get(`/delivery?sort_by=${sortBy}`),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/branding").catch(() => ({ data: {} })),
    ]);
    setItems(r.data);
    setUsers(u.data);
    setAposoftEnabled(b.data?.aposoft?.enabled || false);
  };
  useEffect(() => { load(); }, [sortBy]);

  const drivers = users.filter((x) => x.role !== "kiosk");
  const driverName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? `${u.first_name} ${u.last_name}` : null;
  };

  const doGeocode = async () => {
    if (!form.address) { toast.error("Adresse eingeben"); return; }
    setGeocoding(true);
    try {
      const r = await api.post("/delivery/geocode", { address: form.address });
      setForm({ ...form, latitude: r.data.latitude, longitude: r.data.longitude, distance_km: r.data.distance_km });
      toast.success(`${r.data.distance_km} km ermittelt`);
    } catch (err) { toast.error(err.response?.data?.detail || "Adresse nicht gefunden"); }
    finally { setGeocoding(false); }
  };

  const aposoftSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/delivery/aposoft-sync");
      toast.success(`${r.data.created} neue Bestellungen von Aposoft`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setSyncing(false); }
  };

  const save = async () => {
    try { if (dialog === "create") await api.post("/delivery", form); else await api.put(`/delivery/${form.id}`, form); toast.success("Gespeichert"); setDialog(null); await load(); }
    catch (err) { toast.error("Fehler"); }
  };

  const moveTo = async (item, status) => {
    try { await api.put(`/delivery/${item.id}`, { ...item, status }); await load(); } catch (err) { toast.error("Fehler"); }
  };

  const del = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/delivery/${id}`); await load(); };

  return (
    <div className="space-y-6" data-testid="delivery-root">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <div className="overline">Apotheke</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Botendienst</h2>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {aposoftEnabled && (
            <Button data-testid="del-aposoft-sync" variant="outline" onClick={aposoftSync} disabled={syncing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> Aposoft-Sync
            </Button>
          )}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-52" data-testid="del-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="distance">Sortiert nach Entfernung</SelectItem>
              <SelectItem value="priority">Sortiert nach Priorität</SelectItem>
              <SelectItem value="created">Sortiert nach Alter</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="del-add-btn" onClick={() => { setForm(empty); setDialog("create"); }} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Neue Tour</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {COLS.map((col) => (
          <div key={col.id} className="space-y-2" data-testid={`del-col-${col.id}`}>
            <div className="flex justify-between items-center px-1">
              <h3 className="font-heading font-bold text-sm">{col.label}</h3>
              <Badge variant="secondary" className="text-xs">{items.filter(i => i.status === col.id).length}</Badge>
            </div>
            <div className="space-y-2">
              {items.filter(i => i.status === col.id).map((i) => (
                <Card key={i.id} data-testid={`del-card-${i.id}`} className="card-hover border-border/60">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="font-semibold text-sm flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-blue-600" />{i.customer_name}</div>
                      <div className="flex gap-1">
                        {i.source === "aposoft" && <Badge variant="outline" className="text-[9px] bg-blue-50 dark:bg-blue-950/20">APO</Badge>}
                        {i.priority === "express" && <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 text-[10px]">EXPRESS</Badge>}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{i.address}</div>
                    {i.distance_km > 0 && <div className="text-xs text-blue-700 font-semibold mt-0.5">📍 {i.distance_km.toFixed(1)} km entfernt</div>}
                    {i.phone && <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{i.phone}</div>}
                    {i.delivery_time && <div className="text-xs text-amber-700 flex items-center gap-1"><Clock className="w-3 h-3" /> {i.delivery_date || ""} {i.delivery_time}</div>}
                    {i.assigned_driver_id && <div className="text-xs text-emerald-700 mt-0.5">🚗 {driverName(i.assigned_driver_id)}</div>}
                    {i.items && <div className="text-xs mt-1 text-slate-600 dark:text-slate-300">{i.items}</div>}
                    <div className="flex gap-1 mt-2 pt-2 border-t border-border/60">
                      <Select value={i.status} onValueChange={(v) => moveTo(i, v)}>
                        <SelectTrigger className="h-7 text-xs" data-testid={`del-status-${i.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{COLS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setForm(i); setDialog("edit"); }}>✎</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => del(i.id)}><Trash2 className="w-3 h-3 text-rose-600" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog === "create" ? "Neue Tour" : "Tour bearbeiten"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kunde</Label><Input data-testid="del-form-customer" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
            <div>
              <Label>Adresse</Label>
              <div className="flex gap-2">
                <Input data-testid="del-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <Button type="button" data-testid="del-geocode" variant="outline" onClick={doGeocode} disabled={geocoding} title="Entfernung ermitteln">
                  <Navigation className={`w-4 h-4 ${geocoding ? "animate-pulse" : ""}`} />
                </Button>
              </div>
              {form.distance_km > 0 && <p className="text-xs text-blue-700 mt-1">📍 {form.distance_km.toFixed(1)} km entfernt (via OpenStreetMap)</p>}
            </div>
            <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lieferdatum</Label><Input type="date" data-testid="del-form-date" value={form.delivery_date || ""} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></div>
              <div><Label>Lieferzeit</Label><Input type="time" data-testid="del-form-time" value={form.delivery_time || ""} onChange={(e) => setForm({ ...form, delivery_time: e.target.value })} /></div>
            </div>
            <div>
              <Label>Fahrer</Label>
              <Select value={form.assigned_driver_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_driver_id: v === "none" ? null : v, status: v === "none" ? form.status : (form.status === "new" ? "assigned" : form.status) })}>
                <SelectTrigger data-testid="del-form-driver"><SelectValue placeholder="Kein Fahrer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Fahrer</SelectItem>
                  {drivers.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.position})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Artikel</Label><Textarea value={form.items} onChange={(e) => setForm({ ...form, items: e.target.value })} rows={2} /></div>
            <div><Label>Notizen</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            <div>
              <Label>Priorität</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="express">Express</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Abbrechen</Button>
            <Button data-testid="del-form-save" onClick={save}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
